/* UGC shopping : carrousel natif, lecteur différé et carte produit partagés. */
(() => {
  if (customElements.get('ugc-showcase')) return;
  class UgcShowcase extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.track = this.querySelector('.ugc-showcase__track');
      this.slides = [...this.track.querySelectorAll(':scope > .ugc-story')];
      if (!this.slides.length) return;
      this.initializing = true;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = Math.min(this.index ?? (matchMedia('(max-width: 749px)').matches ? 0 : Math.floor((Number(this.dataset.columns) - 1) / 2)), this.slides.length - 1);
      this.dots = this.querySelector('[data-ugc-dots]');
      this.dots.replaceChildren(...this.slides.map((slide, index) => {
        const button = document.createElement('button');
        button.type = 'button'; button.dataset.ugcIndex = index;
        button.setAttribute('aria-label', this.label(index));
        return button;
      }));
      this.querySelector('.ugc-showcase__nav').hidden = this.slides.length < 2;
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-qb-open]')) this.pauseAll();
        const dot = event.target.closest('[data-ugc-index]');
        if (dot) this.select(Number(dot.dataset.ugcIndex), true);
        if (event.target.closest('[data-ugc-prev]')) this.select(this.index - 1, true);
        if (event.target.closest('[data-ugc-next]')) this.select(this.index + 1, true);
      }, { signal });
      this.track.addEventListener('scroll', () => {
        if (this.initializing) return;
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => { this.frame = null; this.update(this.nearest()); });
      }, { passive: true, signal });
      this.track.addEventListener('keydown', (event) => {
        // Ne pas intercepter les flèches des contrôles vidéo, des variantes ou du dialog.
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const rtl = getComputedStyle(this).direction === 'rtl';
        let index = this.index + ((event.key === 'ArrowRight') !== rtl ? 1 : -1);
        if (event.key === 'Home') index = 0;
        if (event.key === 'End') index = this.slides.length - 1;
        this.select(index, true);
      }, { signal });
      this.track.addEventListener('focusin', (event) => {
        if (event.target.closest('dialog')) return;
        const slide = event.target.closest('.ugc-story');
        if (slide) this.select(this.slides.indexOf(slide), false);
      }, { signal });
      this.addEventListener('play', (event) => {
        const slide = event.target.closest('.ugc-story');
        if (!slide) return;
        this.select(this.slides.indexOf(slide), false);
        slide.classList.add('is-playing');
      }, { capture: true, signal });
      this.addEventListener('pause', (event) => {
        event.target.closest('.ugc-story')?.classList.remove('is-playing');
        this.resetProgress();
      }, { capture: true, signal });
      /* La pastille active se remplit au rythme de la vidéo centrale. */
      this.addEventListener('timeupdate', (event) => {
        const slide = event.target.closest('.ugc-story');
        if (!slide || this.slides.indexOf(slide) !== this.index) return;
        const video = event.target;
        if (!video.duration || !Number.isFinite(video.duration)) return;
        this.setProgress(video.currentTime / video.duration);
      }, { capture: true, signal });
      this.addEventListener('ended', () => this.resetProgress(), { capture: true, signal });
      document.addEventListener('drawer:open', () => this.pauseAll(), { signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.pauseAll(); }, { signal });
      document.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('.ugc-story');
        if (slide && this.contains(slide)) this.select(this.slides.indexOf(slide), false);
      }, { signal });
      this.resize = new ResizeObserver(() => { if (!this.initializing) this.select(this.index, false); });
      this.resize.observe(this.track);
      this.visibility = new IntersectionObserver(([entry]) => { if (!entry.isIntersecting) this.pauseAll(); });
      this.visibility.observe(this);
      this.initialFrame = requestAnimationFrame(() => {
        this.select(this.index, false); this.initializing = false; this.initialFrame = null;
      });
      window.Converto?.ensureQuickBuy?.(this);
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.resize?.disconnect(); this.visibility?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      if (this.initialFrame) cancelAnimationFrame(this.initialFrame);
      this.pauseAll();
    }
    label(index) { return this.dataset.slideLabel.replace('{index}', index + 1).replace('{count}', this.slides.length); }
    nearest() {
      const bounds = this.track.getBoundingClientRect();
      const center = bounds.left + bounds.width / 2;
      let nearest = 0, distance = Infinity;
      this.slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const delta = Math.abs(rect.left + rect.width / 2 - center);
        if (delta < distance) { distance = delta; nearest = index; }
      });
      return nearest;
    }
    select(index, smooth) {
      index = Math.max(0, Math.min(this.slides.length - 1, index));
      const slide = this.slides[index];
      if (!slide) return;
      /* Une vidéo en cours : on enchaîne sur la suivante. L'appel reste dans la
         tâche du clic, la lecture n'est donc pas bloquée par le navigateur. */
      const chain = index !== this.index && this.slides.some((item) => item.classList.contains('is-playing'));
      const track = this.track.getBoundingClientRect();
      const rect = slide.getBoundingClientRect();
      const delta = rect.left + rect.width / 2 - track.left - track.width / 2;
      // Un scroll relatif de moins d'un pixel peut faire sauter au snap précédent.
      if (Math.abs(delta) > 1) this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: smooth && !this.reduced.matches ? 'smooth' : 'instant' });
      this.update(index);
      if (chain) this.playSlide(slide);
    }
    async playSlide(slide) {
      const card = slide.querySelector('.video-card');
      if (!card) return;
      const template = card.querySelector('template[data-video-template]');
      if (template) template.replaceWith(template.content.cloneNode(true));
      const video = card.querySelector('video');
      if (!video) return;
      const play = card.querySelector('.video-card__play');
      try {
        await window.Converto?.startVideo?.(video);
        if (play) play.hidden = true;
        slide.classList.add('is-playing');
      } catch (error) {
        if (play) play.hidden = false;
      }
    }
    update(index) {
      this.index = index;
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        slide.classList.toggle('is-distant', Math.abs(i - index) > 1);
        if (i !== index) this.pause(slide);
      });
      [...this.dots.children].forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.resetProgress();
      this.querySelector('[data-ugc-prev]').disabled = index === 0;
      this.querySelector('[data-ugc-next]').disabled = index === this.slides.length - 1;
      const status = this.querySelector('[data-ugc-status]');
      if (status.textContent !== this.label(index)) status.textContent = this.label(index);
    }
    activeDot() { return this.dots?.children[this.index] || null; }
    setProgress(ratio) {
      const dot = this.activeDot();
      if (!dot) return;
      dot.dataset.progress = '';
      dot.style.setProperty('--ugc-progress', String(Math.max(0, Math.min(1, ratio))));
    }
    resetProgress() {
      [...(this.dots?.children || [])].forEach((dot) => {
        delete dot.dataset.progress;
        dot.style.removeProperty('--ugc-progress');
      });
    }
    pause(slide) {
      const video = slide.querySelector('.video-card video');
      if (!video) return;
      video.pause();
      video.controls = false;
      slide.classList.remove('is-playing');
      const play = slide.querySelector('.video-card__play');
      if (play) play.hidden = false;
    }
    pauseAll() { this.slides?.forEach((slide) => this.pause(slide)); }
  }
  customElements.define('ugc-showcase', UgcShowcase);
})();
